/**
 * DAAily Operations — Google News licensed feed ("Google News feed" page, Oct 2026).
 *
 * The hub publishes RSS 2.0 feeds in the shape of Google's Publisher Center
 * licensed-news spec: a fixed number of editorial articles a day per
 * publication (ArchDaily, designboom), text only (no images for now), each
 * article sent ONCE and never again. Three feeds: combined, and one per
 * publication (`NEWS_FEED_PATHS`), all behind the same set of named access keys
 * (one per consumer, e.g. "Google" and a colleague's test key; each revocable on its own).
 *
 * Data (all server-only collections, see firestore.rules):
 *  - `newsFeedCandidates/{id}` — articles seen in the publications' public feeds
 *    (polled every few hours). Metadata only.
 *  - `newsFeedItems/{id}` — the articles that went into the feed, with the body.
 *    The existence of this doc is the "already sent" lock: an id with an item
 *    doc is never picked or added again, whatever its status.
 *  - `newsFeedMeta/settings` — settings + access key + last run; `newsFeedMeta/pulls`.
 *
 * Doc id = `newsFeedDocId(publication, token)`: ArchDaily keeps the bare numeric
 * article id (the first items were written that way), designboom is `designboom-<post id>`.
 */
import { z } from 'zod';
export const NEWS_FEED_COLLECTIONS = {
    candidates: 'newsFeedCandidates',
    items: 'newsFeedItems',
    meta: 'newsFeedMeta',
};
export const NEWS_FEED_SETTINGS_DOC_ID = 'settings';
export const NEWS_FEED_PULLS_DOC_ID = 'pulls';
export const NEWS_FEED_PUBLICATIONS = [
    { key: 'archdaily', label: 'ArchDaily', site: 'https://www.archdaily.com' },
    { key: 'designboom', label: 'designboom', site: 'https://www.designboom.com' },
];
export const NEWS_FEED_PUBLICATION_KEYS = NEWS_FEED_PUBLICATIONS.map((p) => p.key);
export const newsFeedPublicationLabel = (p) => NEWS_FEED_PUBLICATIONS.find((x) => x.key === p)?.label ?? p;
export const NEWS_FEED_KINDS = ['combined', 'archdaily', 'designboom'];
/** Public paths (firebase.json rewrites `/feeds/**` to `newsFeedXml`). */
export const NEWS_FEED_PATHS = {
    combined: '/feeds/google-news.xml',
    archdaily: '/feeds/google-news-archdaily.xml',
    designboom: '/feeds/google-news-designboom.xml',
};
export function newsFeedKindFromPath(path) {
    const p = path.split('?')[0] ?? path;
    const hit = Object.keys(NEWS_FEED_PATHS).find((k) => NEWS_FEED_PATHS[k] === p);
    return hit ?? null;
}
export function newsFeedDocId(publication, token) {
    return publication === 'archdaily' ? token : `${publication}-${token}`;
}
export const NEWS_FEED_KIND_LABEL = { project: 'Project', news: 'News', sponsored: 'Sponsored', article: 'Article' };
export const NEWS_FEED_EXCLUDED_KINDS = ['article'];
/** Who marked a candidate skipped when it was the exclusion rule, not a person. */
export const NEWS_FEED_RULE_ACTOR = 'rule';
export const NEWS_FEED_DEFAULTS = {
    publications: {
        archdaily: { enabled: false, picksPerDay: 5 },
        designboom: { enabled: false, picksPerDay: 10 },
    },
    minAgeDays: 2,
    maxAgeDays: 6,
    keepDays: 1,
    feedTitle: 'ArchDaily and designboom',
    feedDescription: 'Editorial articles from ArchDaily and designboom, licensed news feed.',
    lastRun: null,
    lastPoll: null,
    updatedAt: null,
    updatedBy: null,
};
export const NEWS_FEED_MAX_SCRAPE_ATTEMPTS = 3;
/** Publication + locator from a public article URL; designboom URLs carry the slug, not the id. */
export function parseNewsFeedArticleUrl(url) {
    const u = url.trim();
    const ad = /^https?:\/\/(?:www\.)?archdaily\.com\/(\d{3,})(?:[/?#]|$)/i.exec(u);
    if (ad?.[1])
        return { publication: 'archdaily', token: ad[1] };
    const db = /^https?:\/\/(?:www\.)?designboom\.com\/(?:[a-z0-9-]+\/)*([a-z0-9-]+)\/?(?:[?#]|$)/i.exec(u);
    if (db?.[1] && !/^(architecture|design|art|technology|readers|tag|page|category)$/i.test(db[1]))
        return { publication: 'designboom', slug: db[1].toLowerCase() };
    return null;
}
/** Kept for the ArchDaily-only callers. */
export function archdailyTokenFromUrl(url) {
    const p = parseNewsFeedArticleUrl(url);
    return p?.publication === 'archdaily' ? p.token : null;
}
/* ───────────── callable request schemas (`operationsNewsFeedApi`) ───────────── */
const DocId = z.string().trim().regex(/^(designboom-)?\d{3,}$/, 'feed item id');
const IsoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const Publication = z.enum(['archdaily', 'designboom']);
const Kind = z.enum(['combined', 'archdaily', 'designboom']);
export const OperationsNewsFeedRequestSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('overview') }),
    z.object({
        action: z.literal('settingsSet'),
        /** With `publication`: `enabled` / `picksPerDay` apply to that publication. */
        publication: Publication.optional(),
        enabled: z.boolean().optional(),
        picksPerDay: z.number().int().min(0).max(30).optional(),
        minAgeDays: z.number().int().min(0).max(30).optional(),
        maxAgeDays: z.number().int().min(1).max(60).optional(),
        keepDays: z.number().int().min(1).max(14).optional(),
        feedTitle: z.string().trim().min(1).max(120).optional(),
        feedDescription: z.string().trim().min(1).max(500).optional(),
    }),
    z.object({ action: z.literal('keyCreate'), label: z.string().trim().min(1).max(60) }),
    z.object({ action: z.literal('keyRotate'), id: z.string().trim().min(1).max(40) }),
    z.object({ action: z.literal('keyRevoke'), id: z.string().trim().min(1).max(40) }),
    z.object({ action: z.literal('keyRename'), id: z.string().trim().min(1).max(40), label: z.string().trim().min(1).max(60) }),
    /** Archive: everything that was ever in a feed, by feed day (max 2,000 rows). */
    z.object({ action: z.literal('archiveList'), from: IsoDay, to: IsoDay, publication: Publication.optional() }),
    z.object({ action: z.literal('archiveExport'), from: IsoDay, to: IsoDay, publication: Publication.optional(), format: z.enum(['csv', 'xml']) }),
    z.object({ action: z.literal('pollNow') }),
    z.object({ action: z.literal('pickNow') }),
    z.object({ action: z.literal('addArticle'), url: z.string().trim().url().max(500) }),
    z.object({ action: z.literal('candidateSkip'), id: DocId, skipped: z.boolean() }),
    z.object({ action: z.literal('itemDelete'), id: DocId }),
    z.object({ action: z.literal('itemRescrape'), id: DocId }),
    z.object({ action: z.literal('previewXml'), kind: Kind.optional() }),
]);
//# sourceMappingURL=operationsNewsFeed.js.map